// SPDX-License-Identifier: BUSL-1.1

/**
 * Typed API client for the build.ceo backend.
 *
 */

import { clearBearerJwt, clearStoredToken, getBearerJwt, getStoredToken, fetchAndStoreBearerJwt } from './auth';
import { Sentry } from './sentry';

/**
 * Resolve the Worker subdomain from the current Pages origin.
 *
 * Mapping (from docs-internal/STAGING_PROD_TOPOLOGY.md):
 *   build.ceo          → api.build.ceo       (production)
 *   console.build.ceo  → api.build.ceo       (production)
 *   *.pages.dev        → api-staging.build.ceo (staging default)
 *   localhost          → /api                 (Vite dev proxy)
 *
 * Note: staging.build.ceo removed 2026-06-28 — use console-staging.build.ceo
 * (the new canonical staging Console) for the staging surface.
 *
 * This runs at runtime so the same build artifact works across all
 * environments without needing VITE_API_URL baked in at build time.
 */
function resolveWorkerBase(): string {
  const host = window.location.hostname;

  // Local dev — Vite proxy handles /api
  if (host === 'localhost' || host === '127.0.0.1') return '/api';

  // build.ceo prod → api.build.ceo
  if (host === 'build.ceo') return 'https://api.build.ceo/api';

  // console.build.ceo prod → api.build.ceo
  if (host === 'console.build.ceo') return 'https://api.build.ceo/api';

  // console-staging.build.ceo (canonical staging Console)
  if (host === 'console-staging.build.ceo') return 'https://api-staging.build.ceo/api';

  // *.pages.dev (CF Pages preview) → staging Worker
  if (host.endsWith('.pages.dev')) return 'https://api-staging.build.ceo/api';

  // Fallback: relative /api (Vite dev proxy)
  return '/api';
}

/**
 * Resolve the build-ceo-auth Worker subdomain from the current Pages origin.
 *
 * Mapping:
 *   build.ceo          → auth.build.ceo       (production)
 *   staging.build.ceo  → auth-staging.build.ceo (staging)
 *   *.pages.dev        → auth-staging.build.ceo (staging default)
 *   localhost          → /api                 (Vite dev proxy)
 */
function resolveAuthBase(): string {
  const host = window.location.hostname;

  if (host === 'localhost' || host === '127.0.0.1') return '/api';
  if (host === 'build.ceo') return 'https://auth.build.ceo';
  if (host === 'console.build.ceo') return 'https://auth.build.ceo';

  // console-staging.build.ceo (canonical staging Console)
  if (host === 'console-staging.build.ceo') return 'https://auth-staging.build.ceo';

  if (host.endsWith('.pages.dev')) return 'https://auth-staging.build.ceo';
  return '/api';
}

/**
 * Base URL for all build.ceo API calls. Resolved at runtime from the current
 * domain so the same build artifact works across staging, production, and
 * preview deployments without needing VITE_API_URL baked in at build time.
 *
 * Override: set VITE_API_URL at build time to force a specific API origin
 * (useful for local dev pointing at a remote Worker).
 */
export const API_BASE = import.meta.env.VITE_API_URL || resolveWorkerBase();

/**
 * Base URL for the standalone build-ceo-auth Better Auth worker.
 * Resolved at runtime; override with VITE_AUTH_URL at build time.
 *
 * Auth requests are formed as `${AUTH_BASE}/api/auth/<endpoint>` since build-ceo-auth
 * mounts Better Auth at `/api/auth/*`.
 */
export const AUTH_BASE = import.meta.env.VITE_AUTH_URL || resolveAuthBase();

type ApiFetchInit = RequestInit & {
  noRedirect?: boolean;
  _retryAfterRefresh?: boolean;
  _queryKey?: string;
};

type ApiErrorStatus = 401 | 403 | 429;

export class ApiError extends Error {
  status: number;
  body: string;
  retryAfter: number | null;
  path: string;
  queryKey?: string;

  constructor(message: string, options: { status: number; body?: string; retryAfter?: number | null; path: string; queryKey?: string }) {
    super(message);
    this.name = 'ApiError';
    this.status = options.status;
    this.body = options.body ?? '';
    this.retryAfter = options.retryAfter ?? null;
    this.path = options.path;
    this.queryKey = options.queryKey;
  }
}

function parseRetryAfter(value: string | null): number | null {
  if (!value) return null;
  const seconds = Number(value);
  return Number.isFinite(seconds) ? seconds : null;
}

function emitApiBreadcrumb(
  category: string,
  data: Record<string, unknown>,
  level: 'info' | 'warning' | 'error' = 'info',
): void {
  Sentry.addBreadcrumb({
    category,
    message: category,
    level,
    data,
  });
}

function captureApiStatusEvent(
  category: string,
  status: ApiErrorStatus,
  path: string,
  init: ApiFetchInit | undefined,
  extra?: Record<string, unknown>,
): void {
  emitApiBreadcrumb(category, {
    path,
    status,
    retryAfter: extra?.retryAfter ?? null,
    queryKey: init?._queryKey ?? null,
    noRedirect: init?.noRedirect ?? false,
    retryAfterRefresh: init?._retryAfterRefresh ?? false,
    ...extra,
  }, status === 429 ? 'warning' : 'info');
}

export function shouldRetryApiQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && [401, 403, 429].includes(error.status)) {
    return false;
  }
  return failureCount < 2;
}

function shouldHandle401WithRedirect(init?: ApiFetchInit): boolean {
  return !init?.noRedirect;
}

export async function apiFetch<T>(
  path: string,
  init?: ApiFetchInit,
): Promise<T> {
  // Prefer the Better Auth JWT when present (humans). Fall back to the
  // localStorage API key (CLI/agent flow) so existing API-key sign-in keeps
  // working until that path is fully retired.
  const jwt = getBearerJwt();
  const apiKey = jwt ? null : getStoredToken();
  const bearer = jwt ?? apiKey;

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'include', // Send session cookies for legacy cookie-only routes.
      headers: {
        'Content-Type': 'application/json',
        ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
        ...init?.headers,
      },
    });
  } catch (err) {
    throw new Error(
      `Cannot reach the build.ceo API. Is the server running? (${err instanceof Error ? err.message : String(err)})`,
      { cause: err },
    );
  }

  if (res.status === 401) {
    captureApiStatusEvent('api-fetch-401', 401, path, init);

    if (init?._retryAfterRefresh) {
      clearBearerJwt();
      clearStoredToken();
      if (shouldHandle401WithRedirect(init)) {
        window.location.href = '/login';
      }
      throw new ApiError('Unauthorized', { status: 401, path, queryKey: init?._queryKey });
    }

    // Try to refresh the JWT from the Better Auth session cookie.
    // This handles the case where JWT expired but session cookie is still valid.
    const refreshed = await fetchAndStoreBearerJwt({ forceRefresh: true });
    if (refreshed) {
      emitApiBreadcrumb('api-fetch-refresh-retry', {
        path,
        queryKey: init?._queryKey ?? null,
      });
      // Retry the original request with the new JWT exactly once.
      return apiFetch<T>(path, {
        ...init,
        _retryAfterRefresh: true,
      });
    }
    // Session refresh failed — clear tokens and handle error
    clearBearerJwt();
    clearStoredToken();
    // Only redirect to login if not explicitly disabled (e.g. for non-critical
    // API calls like GitHub installation status that shouldn't force logout)
    if (shouldHandle401WithRedirect(init)) {
      window.location.href = '/login';
    }
    throw new ApiError('Unauthorized', { status: 401, path, queryKey: init?._queryKey });
  }

  if (res.status === 403) {
    captureApiStatusEvent('api-fetch-403', 403, path, init);
    const body = await res.text().catch(() => '');
    throw new ApiError(`API 403: ${body}`, { status: 403, body, path, queryKey: init?._queryKey });
  }

  if (res.status === 429) {
    const retryAfter = parseRetryAfter(res.headers.get('Retry-After'));
    captureApiStatusEvent('api-fetch-429', 429, path, init, { retryAfter });
    const body = await res.text().catch(() => '');
    throw new ApiError(`API 429: ${body}`, {
      status: 429,
      body,
      retryAfter,
      path,
      queryKey: init?._queryKey,
    });
  }

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new ApiError(`API ${res.status}: ${body}`, {
      status: res.status,
      body,
      path,
      queryKey: init?._queryKey,
    });
  }

  return res.json();
}
